import { EntityService } from "../../../service/EntityService";
import {
  CreateInvoiceSummaryBody,
  GetValidSerialsRes,
  InvoiceSummary,
  ListResponse,
  SummaryDataDetails,
  UpdateInvoiceSummaryBody,
} from "../../../shared/constants";
import { ToastService } from "../../../AppToast";
import { LoadingService } from "../../../AppLoading";
import { localized } from "../../../shared/language";
import { getSummaryErrorMessage } from "./invoiceSummaryUtils";

export class InvoiceSummaryService extends EntityService {
  static entity = "app/summary-data";

  static getInvoiceSummaries = async (
    params: string
  ): Promise<ListResponse<InvoiceSummary>> => {
    LoadingService.start();
    return this.axios
      .get(`${this.entity}`, { params })
      .then((res) => res.data)
      .finally(() => {
        LoadingService.stop();
      });
  };

  static getInvoiceSummaryById = async (
    id: string
  ): Promise<SummaryDataDetails[]> => {
    return this.axios.get(`${this.entity}/${id}`).then((res) => res.data);
  };

  static deleteInvoiceSummary = async (id: string) => {
    return this.axios
      .delete(`${this.entity}/${id}`)
      .then((res) => {
        ToastService.success(localized("report.summaryCreated"));
        return res.data;
      })
      .catch((error) => {
        const errMess = getSummaryErrorMessage(error);

        ToastService.error(errMess);

        throw new Error();
      });
  };

  static createInvoiceSummary = async (data: CreateInvoiceSummaryBody) => {
    LoadingService.start();
    return this.axios
      .post(`${this.entity}`, data)
      .then((res) => {
        ToastService.success(localized("report.summaryCreated"));
        return res.data;
      })
      .catch((error) => {
        const errMess = getSummaryErrorMessage(error);

        ToastService.error(errMess);
        throw new Error();
      })
      .finally(() => LoadingService.stop());
  };

  static updateInvoiceSummary = async (
    id: string,
    data: UpdateInvoiceSummaryBody
  ) => {
    LoadingService.start();
    return this.axios
      .put(`${this.entity}/${id}`, data)
      .then((res) => {
        ToastService.success(localized("report.summaryUpdated"));
        return res.data;
      })
      .catch((error) => {
        const errMess = getSummaryErrorMessage(error);
        ToastService.error(errMess);
        throw new Error();
      })
      .finally(() => LoadingService.stop());
  };
  static supplyInvoiceSummary = async (
    data: UpdateInvoiceSummaryBody,
    id: string
  ) => {
    return this.axios
      .post(`${this.entity}/supplement/${id}`, data)
      .then((res) => {
        ToastService.success(localized("report.summarySupplemented"));
        return res.data;
      })
      .catch((error) => {
        const errMess = getSummaryErrorMessage(error);

        ToastService.error(errMess);
        throw new Error();
      });
  };

  static getInvoiceLookups = async (params: Record<string, unknown>) => {
    LoadingService.start();
    return this.axios
      .post(`${this.entity}/get-list-invoice-lookup`, params)
      .then((res) => res.data)
      .finally(() => LoadingService.stop());
  };

  static getSummaryDetails = async (params: Record<string, unknown>) => {
    return this.axios
      .post(`${this.entity}/prepare-summary-details-from-invoices`, params)
      .then((res) => res.data);
  };

  static getExcelTemplate = async () => {
    return this.axios
      .get(`${this.entity}/excel-template`, { responseType: "blob" })
      .then((res) => res.data);
  };

  static uploadExcel(data: any): Promise<any> {
    return this.axios
      .post(`${this.entity}/upload-excel`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        ToastService.success(localized("report.summaryExcelUploaded"));
        return res.data;
      })
      .catch((error) => {
        const errMess = getSummaryErrorMessage(error);

        ToastService.error(errMess);
        throw new Error();
      });
  }

  static signInvoiceSummary = async (id: string) => {
    return this.axios
      .post(`${this.entity}/sign/${id}`)
      .then((res) => {
        ToastService.success(localized("report.summarySigned"));
        return res.data;
      })
      .catch((error) => {
        const errMess = getSummaryErrorMessage(error);

        ToastService.error(errMess);
        throw new Error();
      });
  };

  static async getXml(id: string): Promise<any> {
    LoadingService.start();
    return this.axios
      .get(`${this.entity}/${id}/download-xml`, {
        responseType: "blob",
      })
      .then((res) => res.data)
      .finally(() => LoadingService.stop());
  }
  static async exportExcel(id: string): Promise<any> {
    LoadingService.start();
    return this.axios
      .get(`${this.entity}/${id}/export-excel`, {
        responseType: "blob",
      })
      .then((res) => res.data)
      .finally(() => LoadingService.stop());
  }

  static downloadInvoiceSummaryPdf = async (id: string) => {
    LoadingService.start();
    return this.axios
      .get(`${this.entity}/${id}/download-pdf`, {
        responseType: "blob",
      })
      .then((res) => res.data)
      .finally(() => LoadingService.stop());
  };
  static getInvoiceSummaryPdfUrl = (id: string) => {
    return `${this.baseURL}/${this.entity}/${id}/download-pdf`;
  };

  static getValidSerials = async () => {
    return this.axios
      .get(`${this.entity}/invoice-serial-use`)
      .then((res) => res.data as GetValidSerialsRes[]);
  };
}
